import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  GraduationCap,
  ShieldCheck,
  Warning,
} from 'phosphor-react';
import { toast } from 'sonner';
// eslint-disable-next-line import/no-extraneous-dependencies
import HCaptcha from '@hcaptcha/react-hcaptcha';
import * as yup from 'yup';
import {
  IEstatutoCampoMeta,
  IEstatutoFormPayload,
  IEstatutoPasso,
  IEstatutoPessoa,
  IEstatutoPublic,
  IPontoConferencia,
  useEstatuto,
} from '../../hooks/useEstatuto';
import { DynamicField, escolhiveis } from './DynamicField';
import { FIELDS_BY_STEP, PASSOS_CONSTITUICAO } from './fieldGroups';
import { EMAIL_REGEX, cpfValido, problemasDaLista, soDigitos } from './PessoasField';
import { paraEnvio, problemasDosPresentes } from './PresentesField';
import { MSG_VAGA, respostaVaga, respostaVagaNoCampo } from './respostaVaga';
import { StepIndicator } from './StepIndicator';

// CNPJ (dígito verificador, como o back).
const pesosCnpj = (n: number) => [
  ...Array.from({ length: n - 8 }, (_, i) => n - 7 - i),
  9,
  8,
  7,
  6,
  5,
  4,
  3,
  2,
];
const cnpjValido = (v: string) => {
  const d = soDigitos(v);
  if (d.length !== 14 || /^(\d)\1+$/.test(d)) return false;
  return [12, 13].every(n => {
    const resto = pesosCnpj(n).reduce((s, p, i) => s + Number(d[i]) * p, 0) % 11;
    return (resto < 2 ? 0 : 11 - resto) === Number(d[n]);
  });
};

// Rascunho no navegador: o formulário é longo (fundadores) e o presidente pode
// fechar a página no meio. Limite: fica só neste navegador; rascunho no back se pedirem.
// Na Baixa, 1 rascunho por etapa (a 2ª parte não herda os campos da 1ª).
const chaveRascunho = (token: string, etapa?: string) =>
  `estatuto-rascunho-${token}${etapa ? `-${etapa}` : ''}`;
const lerRascunho = (
  chave: string,
  campos: IEstatutoCampoMeta[],
): Partial<IEstatutoFormPayload> => {
  try {
    const salvo = JSON.parse(localStorage.getItem(chave) || '{}');
    const porNome = Object.fromEntries(campos.map(m => [m.name, m]));
    // Campo que saiu do formulário ou opção que saiu do select fica de fora.
    return Object.fromEntries(
      Object.entries(salvo).filter(
        ([k, v]) =>
          porNome[k] &&
          (porNome[k].type !== 'select' || (porNome[k].options ?? []).includes(v as string)),
      ),
    );
  } catch {
    return {};
  }
};
const apagarRascunho = (chave: string) => {
  try {
    localStorage.removeItem(chave);
  } catch {
    // sem armazenamento: nada a apagar
  }
};
const gravarRascunho = (chave: string, dados: unknown) => {
  try {
    localStorage.setItem(chave, JSON.stringify(dados));
  } catch {
    // sem armazenamento (aba anônima, cota): segue sem rascunho
  }
};

// Em desenvolvimento desabilitamos totalmente o captcha (mesma regra do login)
const isCaptchaDisabled =
  process.env.NODE_ENV === 'development' || process.env.REACT_APP_DISABLE_CAPTCHA === 'true';

// "X" é valor: turma em romano, a rede X no canal (fora da regra do "só x").
const X_VALE = ['numero_turma', 'canal_comunicacao'];

/**
 * Constrói um schema Yup dinâmico baseado no metadata.
 * Required-ness vem do backend; validações especiais (CPF, email, vigência,
 * eventos não vazio) são adicionadas de forma estática conforme spec.
 */
const buildSchema = (campos: IEstatutoCampoMeta[]) => {
  const shape: Record<string, yup.AnySchema> = {};

  campos.forEach(meta => {
    let s: yup.AnySchema;

    if (meta.type === 'integer') {
      let n = yup
        .number()
        .typeError('Informe um número')
        .integer('Informe um número inteiro')
        .nullable();
      if (meta.min != null) n = n.min(meta.min, `Mínimo ${meta.min}`);
      if (meta.max != null) n = n.max(meta.max, `Máximo ${meta.max}`);
      if (meta.required) n = n.required('Campo obrigatório');
      s = n;
    } else if (meta.type === 'boolean') {
      const b = yup.boolean();
      s = meta.required ? b.required('Campo obrigatório') : b;
    } else if (meta.type === 'list') {
      let arr = yup
        .array()
        .of(yup.string().required())
        .test('vaga', '', function testar(v) {
          const item = (v ?? []).find(x => respostaVaga(x));
          return item
            ? this.createError({
                message: `Tire “${item}” da lista ou escreva só o nome do evento: a lista sai escrita no documento.`,
              })
            : true;
        });
      if (meta.required) {
        arr = arr.min(1, 'Adicione ao menos um item');
      }
      s = arr;
    } else if (meta.type === 'pessoas') {
      s = yup.array().test('pessoas', '', function testar(v) {
        const msg = problemasDaLista((v as any[]) ?? []);
        return msg ? this.createError({ message: msg }) : true;
      });
    } else if (meta.type === 'presentes') {
      s = yup.array().test('presentes', '', function testar(v) {
        const msg = problemasDosPresentes((v as any[]) ?? []);
        return msg ? this.createError({ message: msg }) : true;
      });
    } else if (meta.type === 'pessoa') {
      s = yup
        .string()
        .required('Campo obrigatório')
        .test('presente', 'Escolha entre os presentes', function testar(v) {
          return escolhiveis(this.parent).some(p => soDigitos(p.cpf) === v);
        });
    } else if (meta.type === 'hora') {
      s = yup
        .string()
        .required('Campo obrigatório')
        .matches(/^([01]\d|2[0-3]):[0-5]\d$/, 'Horário inválido');
    } else if (meta.name === 'local' || meta.name === 'plataforma') {
      // Baixa: endereço com assembleia presencial/híbrida; plataforma com virtual/híbrida. Escondido,
      // não confere nada (nem vai para o back: `corpoDoEnvio`).
      const dispensa = meta.name === 'local' ? 'virtual' : 'presencial';
      s = yup
        .string()
        .nullable()
        .when('formato', {
          is: (f: string) => !!f && f !== dispensa,
          then: t => t.required('Campo obrigatório').test('vaga', MSG_VAGA, v => !respostaVaga(v)),
        });
    } else if (meta.name === 'balancete_trimestral_ate') {
      s = yup
        .string()
        .nullable()
        .when('periodicidade_balancete', {
          is: 'trimestral',
          then: t =>
            t
              .required('Informe até quando o balancete é trimestral')
              .test('vaga', MSG_VAGA, v => !respostaVaga(v)),
        });
    } else {
      let str = yup.string();
      if (meta.required) str = str.required('Campo obrigatório');
      // "não sei", "n/a", "?": sairia escrito no documento (o back confere de novo). CPF, e-mail e
      // CNPJ têm a conferência deles; o saldo é número ("0" = não sobrou nada).
      const comFormato =
        meta.name.endsWith('_cpf') ||
        meta.name === 'cnpj' ||
        meta.name === 'email' ||
        meta.name.endsWith('_email');
      if (!comFormato && meta.name !== 'saldo')
        str = str.test(
          'vaga',
          MSG_VAGA,
          v => !respostaVagaNoCampo(meta.name, v, X_VALE.includes(meta.name)),
        );
      // CPF, e-mail e CNPJ conferidos já no passo (o back confere de novo).
      if (meta.name.endsWith('_cpf'))
        str = str.test('cpf', 'CPF inválido', v => !v || cpfValido(v));
      if (meta.name === 'email' || meta.name.endsWith('_email'))
        str = str.test('email', 'E-mail inválido', v => !v || EMAIL_REGEX.test(v.trim()));
      if (meta.name === 'cnpj') str = str.test('cnpj', 'CNPJ inválido', v => !v || cnpjValido(v));
      s = str;
    }

    shape[meta.name] = s;
  });

  // Vigência: fim >= início (permite datas iguais).
  const base = yup.object().shape(shape);
  return base.test('vigencia', 'A data fim deve ser posterior ou igual à data início', value => {
    if (!value) return true;
    const ini = (value as any).vigencia_inicio;
    const fim = (value as any).vigencia_fim;
    if (!ini || !fim) return true;
    return new Date(fim).getTime() >= new Date(ini).getTime();
  });
};

/**
 * Constrói defaults a partir do metadata. Usa `default` do meta se presente,
 * senão tipo-vazio razoável.
 */
const buildDefaults = (
  campos: IEstatutoCampoMeta[],
  preenchidos: IEstatutoFormPayload | null,
): IEstatutoFormPayload => {
  const out: any = {};
  campos.forEach(meta => {
    if (preenchidos && preenchidos[meta.name] !== undefined && preenchidos[meta.name] !== null) {
      out[meta.name] = preenchidos[meta.name];
      return;
    }
    if (meta.type === 'presentes') {
      out[meta.name] = ((meta.default as any[]) ?? []).map(r => ({
        ...r,
        presente: r.presente ?? false,
        adimplente: r.adimplente ?? true,
      }));
      return;
    }
    if (meta.default !== null && meta.default !== undefined) {
      out[meta.name] = meta.default;
      return;
    }
    if (meta.type === 'list' || meta.type === 'pessoas') {
      out[meta.name] = [];
    } else if (meta.type === 'boolean') {
      out[meta.name] = false;
    } else if (meta.type === 'integer') {
      out[meta.name] = null;
    } else {
      out[meta.name] = '';
    }
  });
  return out;
};

// Erro do back com onde está: o passo e o campo, ou o nome da pessoa da lista.
const textoDoErro = (
  d: any,
  campos: IEstatutoCampoMeta[],
  passos: IEstatutoPasso[],
  nomes: string[],
) => {
  const loc: unknown[] = d?.loc ?? [];
  const i = Math.max(loc.indexOf('pessoas'), loc.indexOf('presentes'));
  const campo = String(loc[loc.length - 1] ?? '');
  const rotulo = campos.find(m => m.name === campo)?.label;
  const passo = passos.find(p => p.campos.includes(campo))?.titulo;
  let onde = rotulo ? `${passo ? `${passo} · ` : ''}${rotulo}: ` : '';
  if (i >= 0 && typeof loc[i + 1] === 'number') {
    const n = loc[i + 1] as number;
    onde = `${nomes[n] || `Pessoa ${n + 1}`}${loc[i + 2] ? ` (${loc[i + 2]})` : ''}: `;
  }
  return onde + String(d?.msg ?? '').replace(/^Value error, /, '');
};

const resumoDosFundadores = (pessoas: IEstatutoPessoa[]) => {
  const presidente = pessoas.find(p => p.cargo === 'Presidente');
  const presentes = pessoas.filter(p => p.presente).length;
  const diretoria = pessoas.filter(p => p.cargo).length;
  return `${pessoas.length} (${presentes} presentes, ${diretoria} na diretoria) · Presidente: ${
    presidente?.nome ?? '—'
  }`;
};

interface EstatutoFormProps {
  schema: IEstatutoCampoMeta[];
  passos: IEstatutoPasso[];
  // Revisão com o resumo dos fundadores (constituição).
  revisao: boolean;
  onSubmit: (data: IEstatutoFormPayload) => void;
  // Conferência por IA no clique de enviar (23/10). Nunca falha: sem resposta, volta vazia.
  onConferir: (data: IEstatutoFormPayload) => Promise<IPontoConferencia[]>;
  isSubmitting: boolean;
  chave: string;
}

const EstatutoForm: React.FC<EstatutoFormProps> = ({
  schema,
  passos,
  revisao,
  onSubmit,
  onConferir,
  isSubmitting,
  chave,
}) => {
  const [step, setStep] = useState(1);
  const [conferindo, setConferindo] = useState(false);
  // Última conferência: com os mesmos dados não há outra (o presidente decide com o que veio).
  const [conferido, setConferido] = useState<{
    chave: string;
    dados: IEstatutoFormPayload;
    pontos: IPontoConferencia[];
  } | null>(null);
  const [verPontos, setVerPontos] = useState(false);
  const ULTIMO_PASSO = passos.length;
  // Novo passo: volta ao topo e leva o foco ao título (teclado e leitor de tela).
  const tituloRef = useRef<HTMLHeadingElement>(null);
  const primeiroPasso = useRef(true);
  useEffect(() => {
    if (primeiroPasso.current) {
      primeiroPasso.current = false;
      return;
    }
    window.scrollTo(0, 0);
    tituloRef.current?.focus();
  }, [step]);
  // Endereço e plataforma só aparecem quando o formato pede: aí são obrigatórios.
  const comAsterisco = (meta: IEstatutoCampoMeta) =>
    meta.name === 'local' || meta.name === 'plataforma' ? { ...meta, required: true } : meta;
  const yupSchema = useMemo(() => buildSchema(schema), [schema]);
  const defaults = useMemo(
    () => ({
      ...buildDefaults(schema, null),
      ...lerRascunho(chave, schema),
    }),
    [schema, chave],
  );

  const methods = useForm<IEstatutoFormPayload>({
    resolver: yupResolver(yupSchema as any),
    defaultValues: defaults as any,
    mode: 'onChange',
  });

  useEffect(() => {
    const sub = methods.watch(valores => gravarRascunho(chave, valores));
    return () => sub.unsubscribe();
  }, [methods, chave]);

  const metaByName = useMemo(() => {
    const m: Record<string, IEstatutoCampoMeta> = {};
    schema.forEach(meta => {
      m[meta.name] = meta;
    });
    return m;
  }, [schema]);

  const currentFields = passos[step - 1].campos;
  // "Balancete trimestral até" só faz sentido com o balancete trimestral; na Baixa, o
  // endereço e a plataforma dependem do formato da assembleia.
  const trimestral = methods.watch('periodicidade_balancete') === 'trimestral';
  const formato = methods.watch('formato' as any) as unknown as string | undefined;
  const visivel = (meta: IEstatutoCampoMeta) => {
    if (meta.name === 'balancete_trimestral_ate') return trimestral;
    if (meta.name === 'local') return formato !== 'virtual';
    if (meta.name === 'plataforma') return formato !== 'presencial';
    return true;
  };

  const handleNext = async () => {
    const fieldsToValidate = currentFields as any;
    const ok = await methods.trigger(fieldsToValidate);
    if (!ok) {
      // Campo em branco, inválido ou com "não sei": a mensagem de cada um fica embaixo dele.
      toast.error('Confira os campos marcados antes de continuar');
      return;
    }
    // Validação extra entre vigências no passo 2
    if (step === 2) {
      const ini = methods.getValues('vigencia_inicio');
      const fim = methods.getValues('vigencia_fim');
      if (ini && fim && new Date(fim).getTime() < new Date(ini).getTime()) {
        methods.setError('vigencia_fim', {
          message: 'A data fim deve ser posterior ou igual à data início',
        });
        toast.error('Data fim deve ser posterior ou igual à data início');
        return;
      }
    }
    setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  // A conferência leva segundos: se nesse meio a tela mudou (o presidente mexeu num campo ou
  // reabriu a 1ª parte da Baixa), o resultado não vale e nada é enviado.
  const montado = useRef(true);
  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  const handleFinalSubmit = methods.handleSubmit(
    async data => {
      const chaveDados = JSON.stringify(data);
      let atual = conferido?.chave === chaveDados ? conferido : null;
      if (!atual) {
        const antes = JSON.stringify(methods.getValues());
        setConferindo(true);
        let pontos: IPontoConferencia[] = [];
        try {
          pontos = await onConferir(data);
        } catch {
          // a conferência nunca segura o envio
        }
        if (!montado.current) return;
        setConferindo(false);
        if (JSON.stringify(methods.getValues()) !== antes) {
          toast.info(
            'Os dados mudaram durante a conferência. Clique em Enviar formulário de novo.',
          );
          return;
        }
        atual = { chave: chaveDados, dados: data, pontos };
        setConferido(atual);
      }
      if (atual.pontos.length) setVerPontos(true);
      else onSubmit(data);
    },
    () => toast.error('Verifique os campos do formulário'),
  );

  // "Corrigir": fecha o aviso, vai ao passo do campo e, quando o aviso termina de fechar, leva o
  // foco ao campo (a lista de pessoas não tem um campo só: fica o título do passo).
  const passoDoCampo = (campo: string) => passos.findIndex(p => p.campos.includes(campo));
  const focar = useRef<string | null>(null);
  const corrigir = (ponto: IPontoConferencia) => {
    focar.current = ponto.campo;
    setVerPontos(false);
    const i = passoDoCampo(ponto.campo);
    if (i >= 0) setStep(i + 1);
  };
  const focarCampo = () => {
    const el = focar.current ? document.getElementById(focar.current) : null;
    focar.current = null;
    el?.focus();
  };

  return (
    <FormProvider {...methods}>
      <StepIndicator currentStep={step} titulos={passos.map(p => p.titulo)} />

      <Box mb={5}>
        <Heading
          ref={tituloRef}
          tabIndex={-1}
          outline="none"
          fontSize="lg"
          fontWeight="700"
          color="gray.800"
        >
          {passos[step - 1].titulo}
        </Heading>
        <Text fontSize="sm" color="gray.500" mt={1}>
          {passos[step - 1].descricao}
        </Text>
      </Box>

      <Box as="form" onSubmit={handleFinalSubmit}>
        {step !== ULTIMO_PASSO && (
          <Stack spacing={0}>
            {currentFields
              .map(name => metaByName[name])
              .filter((m): m is IEstatutoCampoMeta => !!m && visivel(m))
              .map(meta => (
                <DynamicField key={meta.name} meta={comAsterisco(meta)} />
              ))}
          </Stack>
        )}

        {step === ULTIMO_PASSO && (
          <>
            <Stack spacing={0}>
              {currentFields
                .map(name => metaByName[name])
                .filter((m): m is IEstatutoCampoMeta => !!m && visivel(m))
                .map(meta => (
                  <DynamicField key={meta.name} meta={comAsterisco(meta)} />
                ))}
            </Stack>

            {revisao && (
              <Box
                mt={6}
                bg="gray.50"
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="xl"
                p={5}
              >
                <Heading fontSize="sm" fontWeight="700" color="gray.700" mb={3}>
                  Revisão antes de enviar
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                  {[...FIELDS_BY_STEP[1], ...FIELDS_BY_STEP[2]]
                    .map(name => metaByName[name])
                    .filter((m): m is IEstatutoCampoMeta => !!m && visivel(m))
                    .map(meta => {
                      const value = methods.getValues(meta.name as any);
                      let display: string;
                      if (Array.isArray(value)) {
                        display = value.length > 0 ? value.join(', ') : '—';
                      } else if (typeof value === 'boolean') {
                        display = value ? 'Sim' : 'Não';
                      } else if (value == null || value === '') {
                        display = '—';
                      } else {
                        display = String(value);
                      }
                      return (
                        <Box key={meta.name}>
                          <Text fontSize="10px" color="gray.500" fontWeight="700" mb={0.5}>
                            {meta.label}
                          </Text>
                          <Text fontSize="xs" color="gray.700">
                            {display}
                          </Text>
                        </Box>
                      );
                    })}
                  <Box>
                    <Text fontSize="10px" color="gray.500" fontWeight="700" mb={0.5}>
                      Fundadores
                    </Text>
                    <Text fontSize="xs" color="gray.700">
                      {resumoDosFundadores(methods.getValues('pessoas') ?? [])}
                    </Text>
                  </Box>
                </SimpleGrid>
              </Box>
            )}
          </>
        )}

        <Flex justify="space-between" align="center" mt={8} gap={3}>
          <Button
            variant="ghost"
            leftIcon={<ArrowLeft size={16} weight="bold" />}
            onClick={handleBack}
            isDisabled={step === 1 || conferindo}
          >
            Voltar
          </Button>
          {/* keys diferentes: sem elas o React reaproveita o <button>, que vira "submit" no
              meio do clique de "Continuar" do penúltimo passo e envia o formulário. */}
          {step < ULTIMO_PASSO ? (
            <Button
              key="continuar"
              colorScheme="blue"
              rightIcon={<ArrowRight size={16} weight="bold" />}
              onClick={handleNext}
            >
              Continuar
            </Button>
          ) : (
            <Button
              key="enviar"
              colorScheme="green"
              type="submit"
              rightIcon={<CheckCircle size={16} weight="bold" />}
              isLoading={isSubmitting || methods.formState.isSubmitting}
              loadingText={conferindo ? 'Conferindo os dados' : undefined}
            >
              Enviar formulário
            </Button>
          )}
        </Flex>
      </Box>

      <Modal
        isOpen={verPontos && !!conferido}
        onClose={() => setVerPontos(false)}
        size="lg"
        scrollBehavior="inside"
        // "Corrigir" leva o foco ao campo; fechar de outro jeito volta ao botão de enviar.
        returnFocusOnClose={!focar.current}
        onCloseComplete={focarCampo}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">Confira antes de enviar</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" color="gray.600" mb={4}>
              Estes dados parecem não estar certos. Corrija o que for preciso ou envie assim mesmo.
            </Text>
            <Stack spacing={3}>
              {conferido?.pontos.map(p => (
                <Flex
                  key={p.caminho}
                  gap={3}
                  align="flex-start"
                  borderWidth="1px"
                  borderColor="orange.200"
                  bg="orange.50"
                  borderRadius="lg"
                  p={3}
                >
                  <Box flex={1} minW={0}>
                    {passoDoCampo(p.campo) >= 0 && (
                      <Text fontSize="10px" color="gray.500" fontWeight="700">
                        {passos[passoDoCampo(p.campo)].titulo}
                      </Text>
                    )}
                    <Text fontSize="xs" color="gray.700" fontWeight="600">
                      {p.onde}
                    </Text>
                    <Text fontSize="sm" color="gray.800" wordBreak="break-word">
                      “{p.valor}”
                    </Text>
                    <Text fontSize="xs" color="orange.800" mt={1}>
                      {p.problema}
                    </Text>
                  </Box>
                  <Button
                    size="xs"
                    variant="outline"
                    aria-label={`Corrigir: ${p.onde}`}
                    onClick={() => corrigir(p)}
                  >
                    Corrigir
                  </Button>
                </Flex>
              ))}
            </Stack>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" onClick={() => setVerPontos(false)}>
              Voltar ao formulário
            </Button>
            <Button
              colorScheme="green"
              onClick={() => {
                setVerPontos(false);
                if (conferido) onSubmit(conferido.dados);
              }}
            >
              Enviar assim mesmo
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </FormProvider>
  );
};

// ============================================================================
// Telas auxiliares
// ============================================================================

const InfoScreen: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  iconBg?: string;
}> = ({ icon, title, description, iconBg = '#EDE9FE' }) => (
  <Flex minH="100vh" align="center" justify="center" bg="gray.50" p={4}>
    <Box
      bg="white"
      p={10}
      borderRadius="2xl"
      boxShadow="0 8px 32px -8px rgba(0,0,0,0.08)"
      textAlign="center"
      maxW="480px"
    >
      <Flex
        w="64px"
        h="64px"
        borderRadius="2xl"
        bg={iconBg}
        align="center"
        justify="center"
        mx="auto"
        mb={5}
      >
        {icon}
      </Flex>
      <Heading fontSize="lg" fontWeight="700" color="gray.800" mb={2}>
        {title}
      </Heading>
      <Text color="gray.500" fontSize="sm" lineHeight="1.6">
        {description}
      </Text>
    </Box>
  </Flex>
);

// ============================================================================
// Page
// ============================================================================

export const EstatutoPublicoPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { getEstatutoPublic, conferirEstatutoPublic, submeterEstatutoPublic } = useEstatuto();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const enviando = useRef(false);
  const [submitted, setSubmitted] = useState(false);
  const [captchaPassed, setCaptchaPassed] = useState(isCaptchaDisabled);
  const [data, setData] = useState<IEstatutoPublic | null>(null);
  const [errorState, setErrorState] = useState<
    'expired' | 'sent' | 'not_found' | 'closed' | 'generic' | null
  >(null);

  useEffect(() => {
    if (!token) {
      setErrorState('not_found');
      setLoading(false);
      return;
    }
    setLoading(true);
    setErrorState(null);
    getEstatutoPublic(token)
      .then(res => {
        // A 1ª parte já está gravada no back: ao reabri-la valem os valores de lá, não um
        // rascunho de outra visita (outro navegador pode ter enviado depois).
        if (res.data.etapa === 'assembleia') apagarRascunho(chaveRascunho(token, 'convocacao'));
        setData(res.data);
      })
      .catch((err: any) => {
        const status = err?.response?.status;
        // 410 do back: "Link expirado." ou já enviado (o envio leva direto para assinatura).
        if (status === 410)
          setErrorState(/expirado/i.test(err?.response?.data?.detail ?? '') ? 'expired' : 'sent');
        else if (status === 409) setErrorState('closed');
        else if (status === 404) setErrorState('not_found');
        else setErrorState('generic');
      })
      .finally(() => setLoading(false));
    // getEstatutoPublic é recriado a cada render (vem do useEstatuto), então
    // não pode entrar nas deps — senão o efeito re-executa em loop e a tela
    // fica piscando em "Carregando formulário". O token é a única dependência real.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const chave = chaveRascunho(token ?? '', data?.etapa);

  // Formulário já enviado (talvez de outro aparelho): o rascunho com dados pessoais sai daqui.
  useEffect(() => {
    if (data?.dados_preenchidos) apagarRascunho(chave);
  }, [data, chave]);

  // Baixa: volta à 1ª parte, com o que já foi gravado, enquanto a 2ª não foi enviada.
  // Clicado durante a conferência da 2ª parte: a conferência que terminar antes da 1ª abrir não envia.
  const corrigindo = useRef(false);
  const corrigirPrimeiraParte = async () => {
    if (!token) return;
    if (enviando.current) {
      toast.info('A 2ª parte já está sendo enviada. Aguarde o envio terminar.');
      return;
    }
    corrigindo.current = true;
    try {
      const res = await getEstatutoPublic(token, 'convocacao');
      setData(res.data);
      window.scrollTo(0, 0);
    } catch {
      toast.error('Não foi possível abrir a 1ª parte. Tente novamente em alguns instantes.');
    } finally {
      corrigindo.current = false;
    }
  };

  // O que vai para o back (no envio e na conferência).
  const corpoDoEnvio = (payload: IEstatutoFormPayload) => {
    // Baixa: só os presentes marcados, sem a marca.
    const corpo: any = { ...payload };
    if (Array.isArray(corpo.presentes)) corpo.presentes = paraEnvio(corpo.presentes);
    // Data opcional em branco vai como nula (o back recusa "").
    (data?.schema_campos ?? []).forEach(m => {
      if (m.type === 'date' && !m.required && corpo[m.name] === '') corpo[m.name] = null;
    });
    // Campo escondido pela escolha de outro (balancete mensal, local da virtual, plataforma da
    // presencial) vai vazio: o que sobrou nele não sai no documento.
    if ('balancete_trimestral_ate' in corpo && corpo.periodicidade_balancete !== 'trimestral')
      corpo.balancete_trimestral_ate = null;
    if ('local' in corpo && corpo.formato === 'virtual') corpo.local = null;
    if ('plataforma' in corpo && corpo.formato === 'presencial') corpo.plataforma = null;
    return corpo;
  };

  // Conferência por IA antes do envio: qualquer falha volta vazia e o formulário segue.
  const conferir = async (payload: IEstatutoFormPayload): Promise<IPontoConferencia[]> => {
    if (!token) return [];
    try {
      return await conferirEstatutoPublic(
        token,
        corpoDoEnvio(payload),
        data?.etapa === 'convocacao' ? 'convocacao' : undefined,
      );
    } catch {
      return [];
    }
  };

  const handleSubmit = async (payload: IEstatutoFormPayload) => {
    // Clique duplo ("Enviar assim mesmo" ainda na tela enquanto o aviso fecha): 1 envio só.
    if (!token || enviando.current || corrigindo.current) return;
    enviando.current = true;
    setSubmitting(true);
    const corpo = corpoDoEnvio(payload);
    const nomes: string[] = (corpo.presentes ?? corpo.pessoas ?? []).map((p: any) => p.nome);
    try {
      // A 1ª parte vai com a etapa: reaberta para correção, ela regrava em vez de valer como a 2ª.
      const res = await submeterEstatutoPublic(
        token,
        corpo,
        data?.etapa === 'convocacao' ? 'convocacao' : undefined,
      );
      apagarRascunho(chave);
      // Baixa: a 1ª parte só grava; a 2ª abre em seguida.
      if (data?.etapa === 'convocacao' && res.data.etapa === 'assembleia') {
        // 1ª parte (re)gravada: se a data ou a hora da assembleia mudou, a da 2ª vem dela, não
        // do rascunho. Sem mudança, fica o que o presidente já ajustou na 2ª (ex.: 2ª convocação).
        const chave2 = chaveRascunho(token, 'assembleia');
        const rascunho2: any = lerRascunho(chave2, res.data.schema_campos);
        const antes = Object.fromEntries((data?.schema_campos ?? []).map(m => [m.name, m.default]));
        if (corpo.data_assembleia !== antes.data_assembleia) delete rascunho2.data_assembleia;
        if (corpo.hora_1a !== antes.hora_1a) delete rascunho2.hora_inicio;
        gravarRascunho(chave2, rascunho2);
        setData(res.data);
        window.scrollTo(0, 0);
        toast.success('1ª parte salva. Agora, os dados da assembleia.');
      } else {
        // Baixa enviada: sai também o rascunho da 1ª reaberta e não regravada (tem e-mails).
        if (data?.etapa) apagarRascunho(chaveRascunho(token, 'convocacao'));
        setSubmitted(true);
      }
    } catch (err: any) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 410) {
        setErrorState('expired');
      } else if (status === 409) {
        setErrorState('closed');
      } else if ((status === 400 || status === 422) && detail) {
        // Conferência do back (lista de pessoas, assembleia, domínio dos e-mails): mostra
        // o motivo. O back responde 400 no erro de validação.
        toast.error('Confira os dados antes de enviar', {
          description: Array.isArray(detail)
            ? detail
                .map(d =>
                  textoDoErro(
                    d,
                    data?.schema_campos ?? [],
                    data?.passos ?? PASSOS_CONSTITUICAO,
                    nomes,
                  ),
                )
                .join(' ')
            : String(detail),
          duration: 15000,
        });
      } else {
        toast.error('Erro ao enviar o formulário. Tente novamente em alguns instantes.');
      }
    } finally {
      enviando.current = false;
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg="gray.50">
        <Stack align="center" spacing={4}>
          <Spinner size="xl" color="blue.500" thickness="3px" />
          <Text color="gray.600">Carregando formulário...</Text>
        </Stack>
      </Flex>
    );
  }

  if (errorState === 'expired') {
    return (
      <InfoScreen
        icon={<Icon as={Warning} boxSize={8} color="orange.500" />}
        iconBg="#FEF3C7"
        title="Link expirado"
        description="Este link de preenchimento não está mais disponível. Entre em contato com o escritório para solicitar um novo."
      />
    );
  }

  if (errorState === 'sent') {
    return (
      <InfoScreen
        icon={<Icon as={CheckCircle} boxSize={8} color="blue.500" />}
        iconBg="#DBEAFE"
        title="Formulário já enviado"
        description="Este formulário já foi enviado e não está mais aberto. Os pedidos de assinatura chegam por e-mail, pela Autentique. Para correções, entre em contato com o escritório."
      />
    );
  }

  if (errorState === 'closed') {
    return (
      <InfoScreen
        icon={<Icon as={Warning} boxSize={8} color="orange.500" />}
        iconBg="#FEF3C7"
        title="Formulário não está mais aberto"
        description="Este formulário já foi enviado ou não está mais disponível para preenchimento. Entre em contato com o escritório se precisar de ajuda."
      />
    );
  }

  if (errorState === 'not_found') {
    return (
      <InfoScreen
        icon={<Icon as={Warning} boxSize={8} color="red.500" />}
        iconBg="#FEE2E2"
        title="Link inválido"
        description="Não encontramos esse formulário. Verifique se o endereço está correto."
      />
    );
  }

  if (errorState === 'generic') {
    return (
      <InfoScreen
        icon={<Icon as={Warning} boxSize={8} color="red.500" />}
        iconBg="#FEE2E2"
        title="Erro ao carregar"
        description="Não foi possível abrir o formulário no momento. Tente novamente em alguns instantes."
      />
    );
  }

  if (!data) return null;

  if (submitted) {
    return (
      <InfoScreen
        icon={<Icon as={CheckCircle} boxSize={8} color="green.500" />}
        iconBg="#DCFCE7"
        title="Formulário enviado com sucesso!"
        description="Recebemos as informações. Os documentos vão para assinatura: cada pessoa recebe por e-mail o link da Autentique para assinar. Você pode fechar esta página."
      />
    );
  }

  if (data.status === 'assinado_finalizado') {
    return (
      <InfoScreen
        icon={<Icon as={CheckCircle} boxSize={8} color="green.500" />}
        iconBg="#DCFCE7"
        title={data.etapa ? 'Baixa já finalizada' : 'Estatuto já finalizado'}
        description="Os documentos desta turma já foram assinados. Não há nada para preencher aqui."
      />
    );
  }

  if (data.dados_preenchidos) {
    return (
      <InfoScreen
        icon={<Icon as={CheckCircle} boxSize={8} color="blue.500" />}
        iconBg="#DBEAFE"
        title="Formulário enviado"
        description="Você já enviou as informações. Para correções, entre em contato com o escritório."
      />
    );
  }

  // Gate de verificação: só libera o formulário após o captcha (desabilitado em dev).
  if (!captchaPassed) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg="gray.50" p={4}>
        <Box
          bg="white"
          p={10}
          borderRadius="2xl"
          boxShadow="0 8px 32px -8px rgba(0,0,0,0.08)"
          textAlign="center"
          maxW="480px"
        >
          <Flex
            w="64px"
            h="64px"
            borderRadius="2xl"
            bg="#EDE9FE"
            align="center"
            justify="center"
            mx="auto"
            mb={5}
          >
            <Icon as={ShieldCheck} boxSize={8} color="#7C3AED" />
          </Flex>
          <Heading fontSize="lg" fontWeight="700" color="gray.800" mb={2}>
            Confirme que você não é um robô
          </Heading>
          <Text color="gray.500" fontSize="sm" lineHeight="1.6" mb={6}>
            Para abrir o formulário {data.titulo ? `de "${data.titulo}"` : 'do estatuto'}, conclua a
            verificação de segurança abaixo.
          </Text>
          <Flex justify="center">
            <HCaptcha
              sitekey={process.env.REACT_APP_HCAPTCHA_SITEKEY ?? ''}
              onVerify={() => setCaptchaPassed(true)}
              onExpire={() => setCaptchaPassed(false)}
              onError={() =>
                toast.error('Erro ao verificar o captcha. Por favor, tente novamente.')
              }
            />
          </Flex>
        </Box>
      </Flex>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50" py={{ base: 6, md: 10 }}>
      <Box maxW="720px" mx="auto" px={{ base: 4, md: 6 }}>
        {/* Header */}
        <Flex align="center" gap={3} mb={6}>
          <Flex w="48px" h="48px" borderRadius="xl" bg="#EDE9FE" align="center" justify="center">
            <GraduationCap size={26} color="#7C3AED" weight="duotone" />
          </Flex>
          <Box>
            <Text
              fontSize="10px"
              color="gray.400"
              fontWeight="700"
              textTransform="uppercase"
              letterSpacing="0.1em"
            >
              Comissão de Formatura
            </Text>
            <Heading fontSize="lg" fontWeight="700" color="gray.800" lineHeight="1.2">
              {data.titulo || 'Estatuto da Comissão de Formatura'}
            </Heading>
            {data.etapa && (
              <Text fontSize="sm" color="purple.600" fontWeight="600" mt={1}>
                {data.etapa === 'convocacao'
                  ? 'Encerramento (baixa) · 1ª parte: associação e convocação da assembleia'
                  : 'Encerramento (baixa) · 2ª parte: a assembleia'}
              </Text>
            )}
            {data.etapa === 'assembleia' && (
              <Button
                size="xs"
                variant="link"
                colorScheme="purple"
                mt={1}
                onClick={corrigirPrimeiraParte}
              >
                Corrigir a 1ª parte (associação e convocação)
              </Button>
            )}
          </Box>
        </Flex>

        <Box
          bg="white"
          borderRadius="2xl"
          boxShadow="0 4px 24px -4px rgba(0,0,0,0.06)"
          p={{ base: 5, md: 8 }}
        >
          <EstatutoForm
            key={data.etapa}
            schema={data.schema_campos}
            passos={data.passos ?? PASSOS_CONSTITUICAO}
            revisao={!data.passos}
            onSubmit={handleSubmit}
            onConferir={conferir}
            isSubmitting={submitting}
            chave={chave}
          />
        </Box>

        <Text fontSize="11px" color="gray.400" textAlign="center" mt={6}>
          Suas informações são protegidas e usadas apenas para a elaboração dos documentos desta
          turma.
        </Text>
      </Box>
    </Box>
  );
};

export default EstatutoPublicoPage;
